import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useGroupMembers,
  useUsersInOrganization,
  useAddGroupMember,
  useRemoveGroupMember
} from "../../../api/hooks";
import { useAlert } from "../../../contexts/AlertContext";
import Modal from "../../_shared/Modal/Modal";
import Input from "../../_shared/Input/Input";
import Button from "../../_shared/Button/Button";
import Badge from "../../_shared/Badge/Badge";
import authAPI from "../../../api/authAPI";
import styles from "./Modal.module.css";

const ManageMembersModal = ({ isOpen, onClose, group, organizationId, canManage }) => {
  const { t } = useTranslation("common");
  const { confirm } = useAlert();
  const [memberEmail, setMemberEmail] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("4"); // Default to read
  const [changingRoleFor, setChangingRoleFor] = useState(null);

  const { data: membersData, refetch: refetchMembers } = useGroupMembers(group?.id, {
    enabled: !!group?.id && isOpen
  });
  const { data: usersData } = useUsersInOrganization(organizationId, {
    enabled: !!organizationId && isOpen
  });

  const addMemberMutation = useAddGroupMember();
  const removeMemberMutation = useRemoveGroupMember();

  const membersResponse = membersData?.data || {};
  const directMembers = membersResponse.directMembers || [];
  const inheritedMembers = membersResponse.inheritedMembers || [];
  const usersInOrg = usersData?.data || [];

  const isSubmitting = addMemberMutation.isLoading || removeMemberMutation.isLoading;

  useEffect(() => {
    if (isOpen) {
      setMemberEmail("");
      setSelectedRoleId("4");
      setChangingRoleFor(null);
    }
  }, [isOpen]);

  const handleAddMember = async () => {
    if (!group || !memberEmail || !selectedRoleId) return;

    try {
      // Send email to server - it will either add existing user or send invitation
      await addMemberMutation.mutateAsync({
        groupId: group.id,
        email: memberEmail.toLowerCase().trim(),
        roleId: parseInt(selectedRoleId)
      });

      refetchMembers();
      setMemberEmail("");
      setSelectedRoleId("4");
    } catch (err) {
      console.error("Add member failed:", err);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!group) return;

    const confirmed = await confirm(
      t("auth.settings.organizations.confirmRemoveMember"),
      t("auth.settings.organizations.removeMemberTitle")
    );

    if (!confirmed) return;

    try {
      await removeMemberMutation.mutateAsync({
        groupId: group.id,
        memberId
      });
      refetchMembers();
    } catch (err) {
      console.error("Remove member failed:", err);
    }
  };

  const handleChangeRole = async (memberId, userId, newRoleId) => {
    if (!group) return;

    setChangingRoleFor(memberId);
    try {
      // Remove and re-add with new role
      await removeMemberMutation.mutateAsync({
        groupId: group.id,
        memberId
      });
      await addMemberMutation.mutateAsync({
        groupId: group.id,
        userId,
        roleId: parseInt(newRoleId)
      });
      refetchMembers();
    } catch (err) {
      console.error("Change role failed:", err);
    } finally {
      setChangingRoleFor(null);
    }
  };

  if (!group) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("auth.settings.organizations.manageGroupTitle", { name: group.name })}
    >
      <div className={styles.modalContent}>
        {/* Direct Members */}
        <div className={styles.section}>
          <h4>
            {t("auth.settings.organizations.directMembers")} ({directMembers.length})
          </h4>
          {directMembers.length > 0 ? (
            <div className={styles.membersList}>
              {directMembers.map((member) => (
                <div key={member.id} className={styles.memberItem}>
                  <div className={styles.memberInfo}>
                    <span className={styles.memberName}>
                      {member.user?.fullName || member.user?.email || (member.email ? `(Invitert: ${member.email})` : 'Ukjent bruker')}
                    </span>
                    {canManage ? (
                      <select
                        className={styles.roleSelect}
                        value={member.role?.id || "4"}
                        onChange={(e) => handleChangeRole(member.id, member.userId, e.target.value)}
                        disabled={changingRoleFor === member.id}
                      >
                        <option value="2">{t("auth.settings.organizations.roles.admin")}</option>
                        <option value="3">{t("auth.settings.organizations.roles.write")}</option>
                        <option value="4">{t("auth.settings.organizations.roles.read")}</option>
                      </select>
                    ) : (
                      <Badge color="blue">
                        {t(`auth.settings.organizations.roles.${member.role?.name}`)}
                      </Badge>
                    )}
                  </div>
                  {canManage && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRemoveMember(member.id)}
                      disabled={isSubmitting || changingRoleFor === member.id}
                    >
                      {t("remove")}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p>{t("auth.settings.organizations.noDirectMembers")}</p>
          )}
        </div>

        {/* Inherited Members */}
        {inheritedMembers.length > 0 && (
          <>
            <hr className={styles.divider} />
            <div className={styles.section}>
              <h4>
                {t("auth.settings.organizations.inheritedMembers")} ({inheritedMembers.length})
              </h4>
              <div className={styles.membersList}>
                {inheritedMembers.map((member) => (
                  <div key={member.id} className={styles.memberItem}>
                    <div className={styles.memberInfo}>
                      <span className={styles.memberName}>
                        {member.user?.fullName || member.user?.email || (member.email ? `(Invitert: ${member.email})` : 'Ukjent bruker')}
                      </span>
                      <Badge color="blue">
                        {t(`auth.settings.organizations.roles.${member.effectiveRole}`)}
                      </Badge>
                      <Badge color="neutral">
                        {member.membershipType === 'inherited_from_parent'
                          ? t("auth.settings.organizations.inheritedFrom.parent")
                          : t("auth.settings.organizations.inheritedFrom.child")}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Add Member */}
        {canManage && (
          <>
            <hr className={styles.divider} />
            <div className={styles.section}>
              <h4>{t("auth.settings.organizations.addMember")}</h4>
              <Input
                label={t("auth.email")}
                type="email"
                value={memberEmail}
                onChange={setMemberEmail}
                placeholder={t("auth.settings.organizations.enterEmail")}
                disabled={isSubmitting}
              />
              <div className={styles.inputGroup}>
                <label>{t("auth.settings.organizations.role")}</label>
                <select
                  className={styles.select}
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="2">{t("auth.settings.organizations.roles.admin")}</option>
                  <option value="3">{t("auth.settings.organizations.roles.write")}</option>
                  <option value="4">{t("auth.settings.organizations.roles.read")}</option>
                </select>
              </div>
              <Button
                variant="primary"
                onClick={handleAddMember}
                disabled={isSubmitting || !memberEmail}
              >
                {addMemberMutation.isLoading
                  ? t("auth.settings.organizations.addingMember")
                  : t("auth.settings.organizations.addMember")}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ManageMembersModal;
